import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { replaceUrl } from 'components/helpers/history';
import {
  buildCarouselPhotos,
  buildGalleryPhotos,
} from 'components/marketplace/helpers/galleryPhotos';
import { removeAddressPreviewParams } from 'components/marketplace/helpers/useSearchQueries';
import {
  buildPropertyAnalyticsValue,
  PropertyAnalytics,
} from 'components/marketplace/helpers/useTrackProperty';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import { FooterCTA } from 'components/marketplace/property/FooterCTA';
import { Lightbox } from 'components/marketplace/property/Lightbox';
import {
  heroWalkthroughVideoUrl,
  pdpHeroMedia,
  pdpMediaAvailability,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { PropertyCTAs } from 'components/marketplace/property/PropertySection/PropertyCTAs';
import { PropertyDetails } from 'components/marketplace/property/PropertySection/PropertyDetails';
import { PropertyStatus } from 'components/marketplace/property/PropertySection/PropertyStatus';
import { PropertySummary } from 'components/marketplace/property/PropertySection/PropertySummary';
import { ShareModal } from 'components/marketplace/property/ShareModal';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';
import { Divider } from 'components/shared/Divider';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';

interface MiniPropertyDetailPageProps {
  property: PropertyDetailsType;
  onClose: () => void;
}

export const MiniPropertyDetailPage = ({ property, onClose }: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const ctaRef = useRef<HTMLDivElement>(null);
  const { inView } = useInViewAfterLoad({ ref: ctaRef, threshold: 0, initialInView: true });

  // Refs mirror the overlay open flags synchronously so our keydown handler
  // always reads the latest value without depending on React state batching.
  const isLightboxOpenRef = useRef(false);
  const isShareModalOpenRef = useRef(false);

  const handleOnClose = useCallback(() => {
    const urlParams = removeAddressPreviewParams();
    // `replaceState` swaps the current history entry in place; it does NOT
    // push a new one, so repeated Escape-close → reopen cycles cannot stack
    // entries and break the browser Back button.
    replaceUrl(`${window.location.pathname}?${urlParams}`);
    onClose();
  }, [onClose]);

  // Floor plans intentionally omitted here: the PhotoCarousel
  // cover-crops portrait plans. They remain accessible via the Lightbox,
  // which renders the full ordered list (listing photos + floor plans) with
  // resizeMode: contain.
  const carouselPhotos = useMemo(
    () => buildCarouselPhotos(property),
    // oxlint-disable-next-line react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Add the missing dependencies rather than suppressing
    [property.photos, property.floorPlanPhotos],
  );
  const streetViewLocation = useMemo(
    () =>
      streetViewLocationFor({
        availableSoon: property.listingState === 'AVAILABLE_SOON',
        photoCount: carouselPhotos.length,
        latitude: property.latitude,
        longitude: property.longitude,
      }),
    [property, carouselPhotos.length],
  );
  // A home painting the street-view fallback does not enter an enhanced-media
  // experiment because the fallback owns the same hero surface.
  const mediaAvailability = useMemo(
    () =>
      pdpMediaAvailability(
        streetViewLocation == null ? heroWalkthroughVideoUrl(property) : null,
        streetViewLocation == null ? property.tour3d : null,
      ),
    [property, streetViewLocation],
  );
  const mediaSlot = usePdpMediaSlot({
    addressToken: property.addressToken,
    availability: mediaAvailability,
    surface: 'map-drawer',
  });

  // Close the mini PDP on Escape. Register in the capture phase so this
  // handler always runs before the Lightbox's document-level
  // bubble-phase keydown listener. That guarantees a stable Escape precedence
  // (Lightbox > ShareModal > mini PDP) regardless of mount order: if the
  // Lightbox is open when Escape is pressed, the ref is still true here, we
  // bail, and the Lightbox's own listener runs next and closes it. Without
  // capture-phase ordering, a flipped mount sequence (StrictMode remount or
  // future code-splitting reshuffle) could let the Lightbox close first,
  // flip the ref to false, and then the mini PDP handler would incorrectly
  // also close the panel on the same keypress.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (isLightboxOpenRef.current || isShareModalOpenRef.current) {
        return;
      }
      if (mediaSlot.mediaState === ListingMediaState.VIDEO) {
        return;
      }
      handleOnClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleOnClose, mediaSlot.mediaState]);

  const analyticsValue = useMemo(
    () => buildPropertyAnalyticsValue(property, 'map-drawer'),
    [property],
  );
  const heroMedia = pdpHeroMedia(property, mediaSlot, 'control', { reelWidth: 854 });
  // Floor plans have no carousel slide but do back the lightbox, so the tap
  // target counts the full gallery rather than carousel slides.
  const galleryPhotoCount = useMemo(() => buildGalleryPhotos(property).length, [property]);

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => {
      const next = !prev;
      isShareModalOpenRef.current = next;
      return next;
    });
  };

  // Separate open/close handlers instead of a toggle: `Lightbox`
  // registers a document-level keydown listener that fires `handleClose()` on
  // Escape regardless of `isOpen`. Passing a toggle would flip a closed
  // lightbox OPEN on Escape while the mini PDP is active.
  const openLightbox = () => {
    isLightboxOpenRef.current = true;
    setSelectedPhotoIndex(0);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    isLightboxOpenRef.current = false;
    setIsLightboxOpen(false);
  };

  return (
    <PropertyAnalytics.Provider value={analyticsValue}>
      <Col className="h-full" key={`mini-pdp-${property.addressToken}`}>
        <MapPhotoCarousel
          photos={carouselPhotos}
          onClose={handleOnClose}
          onOpenShareModal={handleToggleShareModal}
          onPhotoClick={galleryPhotoCount > 0 ? openLightbox : undefined}
          heroOverlay={heroMedia}
          key={`mini-pdp-carousel-${property.addressToken}`}
          emptyFallback={
            streetViewLocation ? (
              <StreetViewPhoto
                latitude={streetViewLocation.latitude}
                longitude={streetViewLocation.longitude}
                alt={streetViewAltText(property)}
                fallbackLabel="Photos coming soon"
                className="h-full w-full"
              />
            ) : null
          }
        />
        <Col className="relative">
          <Col className="px-12 pt-6">
            <PropertyStatus property={property} />
            <PropertySummary property={property} />
            <Col ref={ctaRef}>
              <PropertyCTAs property={property} />
            </Col>
            <Divider className="pt-12" />
            <PropertyDetails property={property} />
          </Col>
          <FooterCTA property={property} isVisible={!inView} />
          {isLightboxOpen && (
            <Lightbox
              handleClose={closeLightbox}
              isOpen={isLightboxOpen}
              property={property}
              selectedPhotoIndex={selectedPhotoIndex}
            />
          )}
        </Col>
        <ShareModal
          key={`share-modal-${property.addressToken}`}
          property={property}
          isOpen={isShareModalOpen}
          onClose={handleToggleShareModal}
        />
      </Col>
    </PropertyAnalytics.Provider>
  );
};
